import { FC, useEffect } from "react";
import { Icon, Select } from "@landtechnologies/components";

import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import {
  PerAreaPriceModes,
  PerUnitPriceModes,
  PriceModes,
  PriceTypes,
  ZooplaPricePerUnitModes,
  ZooplaPricePerAreaModes,
} from "../../../typings";

export interface PriceModeProps {
  priceMode: PriceModes;
  priceType: PriceTypes;
  setPriceMode: (priceMode: PriceModes) => void;
  zooplaLicense?: boolean;
}

export const PriceMode: FC<PriceModeProps> = ({
  priceMode,
  priceType,
  setPriceMode,
  zooplaLicense,
}) => {
  const { t } = useTranslation();

  const PRICE_TEXT = t("comparables.map.sidebar.filters.mode.price.price");
  const EST_PRICE_TEXT = t("comparables.map.sidebar.filters.mode.price.est_price");
  const ZOOPLA_RENTAL_ESTIMATE_TEXT = t(
    "comparables.map.sidebar.filters.mode.price.zoopla_rental_estimate"
  );
  const ZOOPLA_VALUATION_TEXT = t("comparables.map.sidebar.filters.mode.price.zoopla_valuation");

  useEffect(() => {
    const otherPriceType =
      priceType === PriceTypes.PER_AREA ? PriceTypes.PER_UNIT : PriceTypes.PER_AREA;
    const defaultTypes = {
      [PriceTypes.PER_AREA]: PerAreaPriceModes.PPSM,
      [PriceTypes.PER_UNIT]: PerUnitPriceModes.PRICE,
    };
    const availableTypes: Array<Record<PriceTypes, PriceModes>> = [
      {
        [PriceTypes.PER_AREA]: PerAreaPriceModes.PPSM,
        [PriceTypes.PER_UNIT]: PerUnitPriceModes.PRICE,
      },
      {
        [PriceTypes.PER_AREA]: PerAreaPriceModes.EPPSM,
        [PriceTypes.PER_UNIT]: PerUnitPriceModes.EP,
      },
      {
        [PriceTypes.PER_AREA]: ZooplaPricePerAreaModes.RENT_ESTIMATE,
        [PriceTypes.PER_UNIT]: ZooplaPricePerUnitModes.RENT_ESTIMATE,
      },
      {
        [PriceTypes.PER_AREA]: ZooplaPricePerAreaModes.VAL_ESTIMATE,
        [PriceTypes.PER_UNIT]: ZooplaPricePerUnitModes.VAL_ESTIMATE,
      },
    ];
    if (!availableTypes.some((type) => type[priceType] === priceMode)) {
      setPriceMode(
        availableTypes.find((type) => type[otherPriceType] === priceMode)?.[priceType] ||
          defaultTypes[priceType]
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceType]);

  return (
    <div data-testid="price-mode" className="atlas-flex atlas-flex-col atlas-justify-between ">
      <Select
        value={priceMode}
        name="priceMode"
        onValueChange={(v) => {
          setPriceMode(v as PriceModes);
        }}
      >
        <Select.Trigger className="atlas-flex atlas-justify-between atlas-gap-2 atlas-items-center">
          <h4 className="atlas-text-sm atlas-font-medium atlas-my-auto atlas-flex-1 atlas-text-left">
            {t("comparables.map.sidebar.filters.mode.price.title")}
          </h4>
          <div className="atlas-border atlas-border-solid atlas-border-neutral-300 hover:atlas-border-blue-600 atlas-rounded-md atlas-cursor-pointer atlas-px-2">
            <Select.Value />
            <Select.Icon>
              <Icon
                size="sm"
                icon="icon-lt-arrow-down-s-line"
                extraClasses="atlas-pl-1 atlas-font-semibold"
              />
            </Select.Icon>
          </div>
        </Select.Trigger>
        <Select.Content
          side="right"
          avoidCollisions={false}
          position="popper"
          sideOffset={-195}
          alignOffset={20}
          className="atlas-z-30 atlas-text-content-primary"
        >
          <Select.Item
            className="atlas-p-1"
            value={
              priceType === PriceTypes.PER_UNIT ? PerUnitPriceModes.PRICE : PerAreaPriceModes.PPSM
            }
          >
            <Select.ItemText>{PRICE_TEXT}</Select.ItemText>
          </Select.Item>
          <Select.Item
            className="atlas-p-1"
            value={
              priceType === PriceTypes.PER_UNIT ? PerUnitPriceModes.EP : PerAreaPriceModes.EPPSM
            }
          >
            <Select.ItemText>{EST_PRICE_TEXT}</Select.ItemText>
          </Select.Item>
          {zooplaLicense && (
            <>
              <Select.Item
                className="atlas-p-1"
                value={
                  priceType === PriceTypes.PER_UNIT
                    ? ZooplaPricePerUnitModes.RENT_ESTIMATE
                    : ZooplaPricePerAreaModes.RENT_ESTIMATE
                }
              >
                <Select.ItemText>{ZOOPLA_RENTAL_ESTIMATE_TEXT}</Select.ItemText>
              </Select.Item>
              <Select.Item
                className="atlas-p-1"
                value={
                  priceType === PriceTypes.PER_UNIT
                    ? ZooplaPricePerUnitModes.VAL_ESTIMATE
                    : ZooplaPricePerAreaModes.VAL_ESTIMATE
                }
              >
                <Select.ItemText>{ZOOPLA_VALUATION_TEXT}</Select.ItemText>
              </Select.Item>
            </>
          )}
        </Select.Content>
      </Select>
    </div>
  );
};
